<template>
  <div class="drawer-section" :class="sectionClasses" :style="accentStyle">
    <div
      class="drawer-section__header"
      :class="{ 'drawer-section__header--collapsible': collapsible }"
      @click="collapsible && $emit('update:expanded', !expanded)"
    >
      <div class="drawer-section__header-left">
        <slot name="icon">
          <i v-if="icon" class="tim-icons mr-2" :class="icon"></i>
        </slot>
        <span class="text-white">{{ title }}</span>
      </div>
      <div class="drawer-section__header-right">
        <slot name="header-actions"></slot>
        <i
          v-if="collapsible"
          class="tim-icons"
          :class="expanded ? 'icon-minimal-up' : 'icon-minimal-down'"
        ></i>
      </div>
    </div>
    <div v-if="!collapsible || expanded" class="drawer-section__content">
      <slot></slot>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DrawerSection',

  props: {
    title: { type: String, required: true },
    icon: { type: String, default: null },
    collapsible: { type: Boolean, default: false },
    expanded: { type: Boolean, default: true },
    variant: { type: String, default: null }, // 'danger' etc.
    // Any CSS hex color. Paints a left rail, tinted border and wash in that
    // color, for sections that carry a status or need emphasis.
    accent: { type: String, default: null }
  },

  computed: {
    sectionClasses() {
      return {
        [`drawer-section--${this.variant}`]: !!this.variant,
        'drawer-section--accent': !!this.accent
      };
    },
    accentStyle() {
      if (!this.accent) return null;
      const hex = this.accent.replace('#', '');
      const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex;
      const n = parseInt(full, 16);
      if (Number.isNaN(n) || full.length !== 6) return null;
      const rgb = `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
      return { '--section-accent': this.accent, '--section-accent-rgb': rgb };
    }
  }
};
</script>

<style lang="scss" scoped>
.drawer-section {
  margin-bottom: 16px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  overflow: hidden;

  &--danger {
    border-color: rgba(245, 54, 92, 0.2);
    background: rgba(245, 54, 92, 0.03);
  }

  &--accent {
    border-color: rgba(var(--section-accent-rgb), 0.35);
    border-left: 3px solid var(--section-accent);
    background: rgba(var(--section-accent-rgb), 0.06);
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
    background: rgba(255, 255, 255, 0.02);

    i.tim-icons {
      font-size: 14px;
    }

    &--collapsible {
      cursor: pointer;
      transition: background 0.2s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.04);
      }
    }
  }

  &__header-left {
    display: flex;
    align-items: center;
    font-size: 13px;
  }

  &__header-right {
    display: flex;
    align-items: center;
    gap: 6px;

    i.tim-icons {
      font-size: 10px;
      color: rgba(255, 255, 255, 0.5);
    }

    // An icon button in header-actions shrinks to its glyph so it never
    // changes the header height (the default .btn-icon is 38px tall).
    .btn.btn-icon {
      width: 18px;
      min-width: 18px;
      height: 18px;
      padding: 0;
      margin: 0;
      font-size: 13px;
      line-height: 1;

      i.tim-icons {
        font-size: 13px;
        color: inherit;
      }
    }
  }

  &__content {
    padding: 16px;

    // The lead element shouldn't stack its own top margin (e.g. info-hint's
    // built-in 12px) on the section padding — reads as a dead gap under the
    // header. Mirrors CollapsibleSection's body rule.
    > :first-child {
      margin-top: 0;
    }
  }

  // Narrow drawers (BaseDrawer goes full width under 768px) need the chrome to
  // give room back to the controls, otherwise rows wrap.
  @media (max-width: 768px) {
    &__header {
      padding: 10px 12px;
    }

    &__header-left {
      font-size: 12px;
    }

    &__content {
      padding: 12px;
    }
  }

  @media (max-width: 480px) {
    &__header {
      padding: 9px 10px;
    }

    &__header-left {
      font-size: 11.5px;
    }

    &__content {
      padding: 10px;
    }
  }
}


</style>

<style lang="scss">
// Light mode (unscoped so .white-content parent selector works)
.white-content .drawer-section {
  background: #f8f9fa;
  border-color: #e3e3e3;

  &--danger {
    border-color: rgba(245, 54, 92, 0.2);
    background: rgba(245, 54, 92, 0.03);
  }

  &--accent {
    border-color: rgba(var(--section-accent-rgb), 0.35);
    border-left: 3px solid var(--section-accent);
    background: rgba(var(--section-accent-rgb), 0.05);
  }

  .drawer-section__header {
    color: #344675;
    background: #f5f6fa;

    i.tim-icons { color: #9A9A9A; }

    &--collapsible:hover {
      background: #ebedf0;
    }
  }

  .drawer-section__header-right i.tim-icons {
    color: #9A9A9A;
  }

}
</style>
